import React from "react";
import { Repeat } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AvailableTimeSlot } from "../../features/sessions/time-slot/models/AvailableTimeSlot";
import { toYmd } from "../../features/calendar/utils/calendar-range";

/** At most this many free slots per day — the column is for what's booked first. */
const MAX_SLOTS_PER_DAY = 2;

interface CalendarFreeSlotsProps {
  /** All slots the calendar loaded; this component picks its own day out of them. */
  slots: AvailableTimeSlot[];
  day: Date;
  onPick: (day: Date, time: string, duration: number) => void;
}

const pad = (value: number) => String(value).padStart(2, "0");
/** Slots arrive as UTC instants resolved for the viewer's zone — read them back in that zone. */
const slotDate = (slot: AvailableTimeSlot) => new Date(slot.dateStart);
const slotTime = (slot: AvailableTimeSlot) => {
  const date = slotDate(slot);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * The free time in a day column: where there is room to put something. Tapping a
 * slot opens the new-session form already filled in with that day and time.
 */
const CalendarFreeSlots: React.FC<CalendarFreeSlotsProps> = ({ slots, day, onPick }) => {
  const { t, i18n } = useTranslation();
  const ymd = toYmd(day);

  const daySlots = slots
    .filter((slot) => toYmd(slotDate(slot)) === ymd)
    // Slots that repeat weekly are the ones worth committing a session to.
    .sort((a, b) => Number(b.isWeekly) - Number(a.isWeekly))
    .slice(0, MAX_SLOTS_PER_DAY);

  if (daySlots.length === 0) {
    return null;
  }

  const timeFormat = new Intl.DateTimeFormat(i18n.language, { hour: "numeric", minute: "2-digit" });

  return (
    <div className="mt-1 space-y-0.5">
      {daySlots.map((slot) => (
        <button
          key={slot.dateStart}
          type="button"
          onClick={() => onPick(day, slotTime(slot), slot.duration)}
          title={t("calendar.freeSlot.hint", "Free — schedule something here")}
          className="flex w-full items-center gap-1 rounded-lg border border-dashed border-grey-90 px-2 py-1 text-xs text-grey-40 hover:border-primary-40 hover:text-primary-40 dark:border-primary-20 dark:text-grey-60 dark:hover:border-primary-90 dark:hover:text-primary-90"
        >
          <span>{timeFormat.format(slotDate(slot))}</span>
          {slot.isWeekly && (
            <Repeat size={11} className="shrink-0 opacity-70" aria-label={t("timeSlots.weekly")} />
          )}
          <span className="ms-auto">{t("calendar.freeSlot.label", "Free")}</span>
        </button>
      ))}
    </div>
  );
};

export default CalendarFreeSlots;
